import { z } from "zod";

// =============================================================================
// Common primitives shared between trip + patient schemas.
// =============================================================================

/** Stored / on-the-wire date format. Calendar dates only, no timezone. */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/u, "Date must be YYYY-MM-DD");
export type IsoDate = z.infer<typeof isoDateSchema>;

/**
 * Reduce any date-ish string to its leading calendar date `YYYY-MM-DD`.
 *
 * `transport_date` is a calendar date, and every date-range filter
 * (Bookings From/To, the Reports spend window, the CSV export, the
 * monthly report) compares it against a `YYYY-MM-DD` bound. A value or
 * bound that carries a TIME component — e.g. a full ISO instant
 * `"2026-06-04T00:00:00.000Z"` or a SQL timestamp `"2026-06-04 00:00:00"`
 * stamped by a write path other than the original plain-`YYYY-MM-DD`
 * one — must still be matched by its calendar date, not silently dropped
 * (a timestamp value at the window's upper bound compares GREATER than the
 * bare-date bound and falls out of an inclusive `<= dateTo`).
 *
 * This collapses such a value to its calendar date by a pure leading-slice
 * of the `YYYY-MM-DD` prefix — deliberately NO `new Date()` / timezone
 * math, so an early-morning UTC instant can never roll back to the
 * previous day (the exact mis-stamp the sync's service-date fixes chased).
 * Returns `""` when there's no leading `YYYY-MM-DD` to read.
 */
export function isoDateOnly(value: string | null | undefined): string {
  const m = String(value ?? "")
    .trim()
    .match(/^(\d{4}-\d{2}-\d{2})/u);
  return m ? m[1]! : "";
}

/** 24-hour time `HH:MM`. Empty string allowed for "no time". */
export const time24Schema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/u, "Time must be HH:MM (24h)");
export type Time24 = z.infer<typeof time24Schema>;

/** Time field that may be empty (intentionally absent) or a sentinel literal. */
export const time24OrEmptySchema = z.union([time24Schema, z.literal("")]);

/** Time field as it travels in trip payloads — either HH:MM, "WILL_CALL", or empty. */
export const pickupTimeWireSchema = z.union([
  time24Schema,
  z.literal("WILL_CALL"),
  z.literal(""),
]);
export type PickupTimeWire = z.infer<typeof pickupTimeWireSchema>;

/** US phone number, exactly 10 digits when stripped of formatting. */
export const usPhoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/gu, ""))
  .pipe(z.string().regex(/^\d{10}$/u, "Phone must be 10 digits"));

/**
 * Reduce a phone number to bare 10 digits WHEN THAT IS UNAMBIGUOUS, otherwise
 * hand back the input untouched.
 *
 * Only two shapes are considered unambiguous:
 *   * exactly 10 digits (any punctuation) → the digits
 *   * exactly 11 digits starting with "1" (US country code) → drop the 1
 *
 * Anything else — 7 digits, an extension, two numbers run together — is
 * returned VERBATIM. Guessing which 10 of 13 digits were meant would silently
 * corrupt a real number, and a wrong phone on a patient transport is worse
 * than a flagged one: the Members review area's "Invalid phone" detector
 * (digit count <> 10) keeps firing so a human can correct it deliberately.
 *
 * Why this exists: the V2 booking form cannot produce a bad phone —
 * `usPhoneSchema` strips and enforces 10 digits. But the TripLink sync writes
 * the feed's `contactPhones[0]` / `callbackPhone` VERBATIM (the wire schema is
 * a bare `z.string()`), and TripLink's own parser pulls `callbackPhone` out of
 * free-text trip notes. That is where the "extra digits within the phone
 * number section" Terrence reported on 2026-09-02 come from. Normalizing at
 * the point of ingest stops new ones without inventing data.
 *
 * Pure, total, and never throws — safe on any wire value.
 */
export function normalizeUsPhone(raw: string | null | undefined): string {
  const value = String(raw ?? "");
  const digits = value.replace(/\D/gu, "");
  if (digits.length === 10) return digits;
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return value;
}

/** Optional text field — allows empty string and trims. */
export const optionalTrimmedString = (max: number) =>
  z
    .string()
    .max(max, `Must be ${max} characters or fewer`)
    .transform((v) => v.trim())
    .optional()
    .default("");

/** Loose email check — same shape the form uses (local@domain.tld, TLD ≥ 2). */
export const emailSchema = z
  .string()
  .max(100)
  .refine(
    (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/u.test(v),
    "Please enter a valid email (e.g. name@example.com).",
  );

/** Captured Google Places result. Frontend may pass null when the user typed
 *  the address by hand and we never resolved a place. */
export const googlePlaceSchema = z.object({
  lat: z.number().nullable(),
  lon: z.number().nullable(),
  buildingNumber: z.string().optional().default(""),
  line1: z.string().optional().default(""),
  // Apartment/suite/unit ("subpremise" in Google's address components). Kept
  // so it isn't dropped when a user picks an address from the dropdown.
  subpremise: z.string().optional().default(""),
  city: z.string().optional().default(""),
  state: z.string().optional().default(""),
  postalCode: z.string().optional().default(""),
  formatted: z.string().optional().default(""),
});
export type GooglePlace = z.infer<typeof googlePlaceSchema>;
export const googlePlaceOrNullSchema = googlePlaceSchema.nullable();
